/* eslint-disable @stylistic/max-len */
import React, {useCallback, useContext, useEffect, useState} from 'react';

import {NumberInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {CheckBox} from '@pb/kit-component-checkbox';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import TreeList, {type TreeData, TreeListBranches} from '@pb/kit-component-tree-list';
import {LocaleContext} from '@pb/kit-context-locale';

import {callCostTableEnabledCell} from './call-cost-table.module.styl';

import type {CallsTreeItem} from './typedef';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {CallCost} from 'src/openapi/admin/models/CallCost';
import type {CallCostGroup} from 'src/openapi/admin/models/CallCostGroup';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    callsCosts: CallCostGroup[],
    defaultCallCosts: CallCost[],
    onChange: (callsCosts: CallCostGroup[]) => void,
    disabled?: boolean,
};

export const CallCostTable = ({callsCosts, onChange, disabled, isLoading, loadingError, defaultCallCosts}: Props) => {
    const {locale} = useContext(LocaleContext);
    const [treeData, setTreeData] = useState<TreeData<CallsTreeItem>[]>([]);

    useEffect(() => {
        setTreeData(prevState => callsCosts.map(group => (
            {
                id: group.group,
                expanded: prevState.find(({id}) => id === group.group)?.expanded ?? false,
                data: {name: group.groupCaption, groupId: group.group},
                children: [
                    ...group.accessPackageInfo ? [{
                        id: `${group.group}-${group.accessPackageInfo.accessPackageTypeName}`,
                        data: {name: 'Accessibility', groupId: group.group},
                        expanded: prevState.find(({id}) => id === `${group.group}-${group.accessPackageInfo?.accessPackageTypeName}`)?.expanded ?? true,
                        children: group.accessPackageInfo.accessOptions.map(option => ({
                            id: `${group.group}-${group.accessPackageInfo?.accessPackageTypeName}-${option.name}`,
                            data: {
                                name: option.description,
                                groupId: group.group,
                                accessOptionId: option.name,
                                accessPackageId: group.accessPackageInfo?.accessPackageTypeName,
                                accessOptionEnabled: option.enabled,
                            },
                            expanded: prevState.find(({id}) => id === `${group.group}-${group.accessPackageInfo?.accessPackageTypeName}-${option.name}`)?.expanded ?? false,
                            children: [],
                        })),
                    }] : [],
                    ...group.callCoasts.map(cost => ({
                        id: `${group.group}-${cost.type}`,
                        expanded: prevState.find(({id}) => id === `${group.group}-${cost.type}`)?.expanded ?? true,
                        data: {
                            groupId: group.group,
                            name: cost.typeCaption,
                            costId: cost.type,
                            costEnabled: cost.enabled,
                            availableEndpoint: cost.availableEndpoint,
                            initialCost: cost.initialCost,
                            refreshCost: cost.refreshCost,
                            defaultInitialCost: defaultCallCosts.find(({type}) => type === cost.type)?.initialCost,
                            defaultRefreshCost: defaultCallCosts.find(({type}) => type === cost.type)?.refreshCost,
                            disabled: !cost.editable,
                        },
                        children: [],
                    })),
                ],
            }
        )));
    }, [callsCosts, defaultCallCosts, onChange]);

    const changeCostEnable = useCallback((groupId: string, costId: string, enabled: boolean) => {
        let changedGroupId;
        let changedGroup = callsCosts.find((group, id) => {
            if (group.group === groupId) {
                changedGroupId = id;
                return true;
            } else {
                return false;
            }
        });

        if (!changedGroup || changedGroupId === undefined) {
            return;
        }

        changedGroup = {
            ...changedGroup,
            callCoasts: changedGroup.callCoasts.map(cost => (cost.type !== costId
                ? cost
                : {...cost, enabled}
            )),
        };
        const changedList = [...callsCosts];
        changedList[changedGroupId] = changedGroup;
        onChange(changedList);
    }, [callsCosts, onChange]);

    const changeAccessOption = useCallback((groupId: string, accessPackageId: string, accessOptionId: string) => {
        let changedGroupId;
        let changedGroup = callsCosts.find((group, id) => {
            if (group.group === groupId) {
                changedGroupId = id;
                return true;
            } else {
                return false;
            }
        });

        if (!changedGroup || changedGroupId === undefined || !changedGroup.accessPackageInfo) {
            return;
        }

        changedGroup = {
            ...changedGroup,
            accessPackageInfo: {
                accessPackageTypeName: accessPackageId,
                accessOptions: changedGroup.accessPackageInfo?.accessOptions.map(option => (
                    option.name !== accessOptionId
                        ? {...option, enabled: false}
                        : {...option, enabled: true}
                )),
            },
        };

        const changedList = [...callsCosts];
        changedList[changedGroupId] = changedGroup;
        onChange(changedList);
    }, [callsCosts, onChange]);

    const changeInitialCost = useCallback((groupId: string, costId: string, initialCost: number) => {
        let changedGroupId;
        let changedGroup = callsCosts.find((group, id) => {
            if (group.group === groupId) {
                changedGroupId = id;
                return true;
            } else {
                return false;
            }
        });

        if (!changedGroup || changedGroupId === undefined) {
            return;
        }

        changedGroup = {
            ...changedGroup,
            callCoasts: changedGroup.callCoasts.map(cost => (cost.type !== costId
                ? cost
                : {...cost, initialCost}
            )),
        };
        const changedList = [...callsCosts];
        changedList[changedGroupId] = changedGroup;
        onChange(changedList);
    }, [callsCosts, onChange]);

    const changeRefreshCost = useCallback((groupId: string, costId: string, refreshCost: number) => {
        let changedGroupId;
        let changedGroup = callsCosts.find((group, id) => {
            if (group.group === groupId) {
                changedGroupId = id;
                return true;
            } else {
                return false;
            }
        });

        if (!changedGroup || changedGroupId === undefined) {
            return;
        }

        changedGroup = {
            ...changedGroup,
            callCoasts: changedGroup.callCoasts.map(cost => (cost.type !== costId
                ? cost
                : {...cost, refreshCost}
            )),
        };
        const changedList = [...callsCosts];
        changedList[changedGroupId] = changedGroup;
        onChange(changedList);
    }, [callsCosts, onChange]);

    return (
        <Skeleton shown={isLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.API_V2_SCREEN_VIEW as AccessRight}>
                <TreeListBranches tree={treeData}>
                    {({setNode}) => (
                        <Table>
                            <Head>
                                <Cell verticalAlign="middle">Name</Cell>
                                <Cell width={70} className="text-align_center">Enabled</Cell>
                                <Cell width={110} className="text-align_center">Initial Cost</Cell>
                                <Cell width={110} className="text-align_center">Refresh Cost</Cell>
                            </Head>
                            <Body>
                                {treeData.map((data, index) => (
                                    <TreeList<CallsTreeItem>
                                        key={data.id}
                                        data={data}
                                        chain={`${index}`}
                                        setNode={setNode}
                                        renderRow={(rowData, Component) => {
                                            const {
                                                name,

                                                accessPackageId,
                                                accessOptionId,
                                                accessOptionEnabled,

                                                costId,
                                                costEnabled,
                                                availableEndpoint,
                                                initialCost,
                                                refreshCost,

                                                defaultInitialCost,
                                                defaultRefreshCost,

                                                disabled: costDisabled,
                                            } = rowData.data;

                                            return (
                                                <Row key={rowData.id}>
                                                    <Cell>
                                                        <Component>
                                                            {name}
                                                        </Component>
                                                    </Cell>
                                                    <Cell className={callCostTableEnabledCell}>
                                                        {(costEnabled !== undefined && costId) ? (
                                                            <Item className="group-item pdy-s">
                                                                <CheckBox
                                                                    disabled={disabled || !availableEndpoint}
                                                                    checked={costEnabled}
                                                                    onCheckBoxChange={changeData => {
                                                                        changeCostEnable(
                                                                            rowData.data.groupId,
                                                                            costId,
                                                                            changeData.event.currentTarget.checked,
                                                                        );
                                                                    }}
                                                                />
                                                            </Item>
                                                        ) : null}
                                                        {(accessOptionEnabled !== undefined && accessPackageId) ? (
                                                            <Item className="group-item pdx-xs">
                                                                <RadioButtons
                                                                    disabled={disabled}
                                                                    name={accessOptionId || ''}
                                                                    value={accessOptionEnabled ? accessOptionId : ''}
                                                                    onChange={value => {
                                                                        changeAccessOption(
                                                                            rowData.data.groupId,
                                                                            accessPackageId,
                                                                            value || '',
                                                                        );
                                                                    }}>
                                                                    <RadioButton
                                                                        value={accessOptionId}
                                                                        showLabel={false}
                                                                    />
                                                                </RadioButtons>
                                                            </Item>
                                                        ) : null}
                                                    </Cell>
                                                    <Cell>
                                                        {(initialCost !== undefined && costId) ? (
                                                            <NumberInput
                                                                readOnly={disabled || costDisabled}
                                                                onBlur={value => {
                                                                    changeInitialCost(rowData.data.groupId, costId, value || 0);
                                                                }}
                                                                value={initialCost}
                                                                maxValue={9999}
                                                                displayedValue={Integer.format(initialCost, locale)}
                                                                endIcon={defaultInitialCost !== undefined && defaultInitialCost !== initialCost ? (
                                                                    <Tooltip
                                                                        color="light"
                                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                                            <span ref={anchorRef} {...getAnchorProps()}>
                                                                                <Icon type={Icons.COGS}/>
                                                                            </span>
                                                                        )}>
                                                                        <Group direction="v" gap="xs">
                                                                            <div>
                                                                                <Icon type={Icons.COGS}/> Custom: <b>{Integer.format(initialCost, locale)}</b>
                                                                            </div>
                                                                            <Item className="group-item pdx-m">
                                                                                Default: <b>{Integer.format(defaultInitialCost, locale)}</b>
                                                                            </Item>
                                                                        </Group>
                                                                    </Tooltip>
                                                                ) : undefined}
                                                            />
                                                        ) : null}
                                                    </Cell>
                                                    <Cell>
                                                        {(refreshCost !== undefined && costId) ? (
                                                            <NumberInput
                                                                readOnly={disabled || costDisabled}
                                                                onBlur={value => {
                                                                    changeRefreshCost(rowData.data.groupId, costId, value || 0);
                                                                }}
                                                                value={refreshCost}
                                                                maxValue={9999}
                                                                displayedValue={Integer.format(refreshCost, locale)}
                                                                endIcon={defaultRefreshCost !== undefined && defaultRefreshCost !== refreshCost ? (
                                                                    <Tooltip
                                                                        color="light"
                                                                        anchor={({anchorRef, getAnchorProps}) => (
                                                                            <span ref={anchorRef} {...getAnchorProps()}>
                                                                                <Icon type={Icons.COGS}/>
                                                                            </span>
                                                                        )}>
                                                                        <Group direction="v" gap="xs">
                                                                            <div>
                                                                                <Icon type={Icons.COGS}/> Custom: <b>{Integer.format(refreshCost, locale)}</b>
                                                                            </div>
                                                                            <Item className="group-item pdx-m">
                                                                                Default: <b>{Integer.format(defaultRefreshCost, locale)}</b>
                                                                            </Item>
                                                                        </Group>
                                                                    </Tooltip>
                                                                ) : undefined}
                                                            />
                                                        ) : null}
                                                    </Cell>
                                                </Row>
                                            );
                                        }}
                                        onExpand={expandData => {
                                            setTreeData(TreeList.expandOnlyTreeNodes(treeData, expandData, data.id));
                                        }}
                                    />
                                ))}
                            </Body>
                        </Table>
                    )}
                </TreeListBranches>
            </ServerResponseHandler>
        </Skeleton>
    );
};
